import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Phone, Mail } from "lucide-react";
import { WhyChooseUsSection } from "../components/WhyChooseUsSection";
import { usePageMeta } from "../hooks/usePageMeta";

const AUDIENCE_IMAGE = "https://images.unsplash.com/photo-1753351052277-a7ff634f0bd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHNtYWxsJTIwYnVzaW5lc3MlMjBvd25lciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MjcyNTU1NHww&ixlib=rb-4.1.0&q=85";

const whoWeServe = [
  "Small businesses (2 to 50 employees)",
  "Mid-size companies (50 to 200 employees)",
  "New businesses offering benefits for the first time",
  "Businesses looking to improve current coverage",
];

const teamMembers = [
  { name: "Joey Viall", role: "Owner / Broker", initials: "JV", photo: "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/t4rh0szy_image0%20%287%29.jpeg", email: "office@viallbusiness.com", phone: "7017217993", phoneDisplay: "(701) 721-7993" },
  { name: "Rebecca Viall", role: "Broker", initials: "RV", photo: null, email: "rebecca.viall@coloniallifesales.com", phone: "7017218195", phoneDisplay: "(701) 721-8195" },
  { name: "Levi Bachman", role: "Broker", initials: "LB", photo: "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/vws6wb2r_image0%20%284%29.jpeg%20updated.jpeg", email: "levi.bachman@viallbusiness.com", phone: null },
  { name: "Trevor Weaver", role: "Broker", initials: "TW", photo: "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/qqgppwlt_trevor%20headshot.PNG", email: "trevor.weaver@viallbusiness.com", phone: null },
  { name: "Jason Adams", role: "Broker", initials: "JA", photo: "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/dh506vfr_image0%20%288%29.jpeg", email: "jason.adams@coloniallifesales.com", phone: null },
];

// Sub-components
const TeamMemberCard = ({ member, index }) => (
  <div data-testid={`team-card-${index}`} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all text-center flex flex-col">
    <div className="w-full h-48 overflow-hidden rounded-xl mx-auto mb-4 flex-shrink-0">
      {member.photo
        ? <img src={member.photo} alt={member.name} className="w-full h-full object-contain object-center" />
        : <div className="w-full h-full bg-[#1A2744] flex items-center justify-center">
            <span className="text-[#2EC4C4] font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{member.initials}</span>
          </div>
      }
    </div>
    <h3 className="font-bold text-base text-[#1A2744] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{member.name}</h3>
    <p className="text-[#2EC4C4] text-sm font-semibold mb-3">{member.role}</p>
    <div className="space-y-1 mb-4 flex-1">
      {member.phone && (
        <a href={`tel:+1${member.phone}`} className="flex items-center justify-center space-x-1 text-xs text-[#475569] hover:text-[#2EC4C4] transition-colors">
          <Phone size={12} /><span>{member.phoneDisplay}</span>
        </a>
      )}
      <a href={`mailto:${member.email}`} className="flex items-center justify-center space-x-1 text-xs text-[#475569] hover:text-[#2EC4C4] transition-colors break-all">
        <Mail size={12} /><span>{member.email}</span>
      </a>
    </div>
    <a href={`mailto:${member.email}`} data-testid={`contact-btn-${index}`} className="block w-full bg-[#2EC4C4] hover:bg-[#25A0A0] text-white text-sm font-semibold rounded-lg py-2.5 transition-all active:scale-95">
      Contact Me
    </a>
  </div>
);

const MissionSection = () => (
  <section data-testid="about-mission" className="py-20 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-6 text-center">
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Mission</h2>
        <p className="text-[#475569] text-base leading-relaxed">At Viall Business Solutions, our mission is simple: help small businesses access the same quality employee benefits that larger corporations offer — without the complexity or high costs.</p>
        <p className="text-[#475569] text-base leading-relaxed">We believe every employee deserves access to quality dental, vision, health, and supplemental insurance. And every small business owner deserves a trusted partner who makes that possible.</p>
        <p className="text-[#475569] text-base leading-relaxed">When you work with us, you work directly with our team — people invested in your success.</p>
        <div className="bg-[#EAF9F9] rounded-2xl p-6 border-l-4 border-[#2EC4C4] text-left max-w-2xl mx-auto">
          <p className="text-[#1A2744] font-semibold italic text-lg">"Enhance Your Benefits. Keep Your Bottom Line."</p>
        </div>
      </div>
    </div>
  </section>
);

const TeamSection = () => (
  <section data-testid="about-team" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Meet Our Team</h2>
        <p className="text-[#475569] text-lg max-w-2xl mx-auto">When you work with Viall Business Solutions, you work directly with our experienced brokers — real people who care about your business.</p>
      </div>
      <div className="flex flex-wrap justify-center gap-6">
        {teamMembers.map((member, i) => (
          <div key={member.name} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
            <TeamMemberCard member={member} index={i} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhoWeServeSection = () => (
  <section data-testid="about-serve" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
          <img src={AUDIENCE_IMAGE} alt="Small business owners" className="w-full h-[380px] object-cover" />
        </div>
        <div className="space-y-6 order-1 lg:order-2">
          <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>Who We Serve</h2>
          <p className="text-[#475569] text-base leading-relaxed">We specialize in serving small and mid-size businesses in North Dakota — restaurants, clinics, retail shops, and service businesses that are the backbone of our communities.</p>
          <p className="text-[#475569] text-base leading-relaxed">Whether you have 2 employees or 200, we have solutions that fit your size, budget, and goals.</p>
          <ul className="space-y-3">
            {whoWeServe.map((item) => (
              <li key={item} className="flex items-center space-x-3">
                <CheckCircle size={18} className="text-[#2EC4C4] flex-shrink-0" />
                <span className="text-[#0F172A]">{item}</span>
              </li>
            ))}
          </ul>
          <Link to="/contact" data-testid="about-contact-btn" className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-3 transition-all shadow-md hover:shadow-lg active:scale-95">
            <span>Get Started Today</span><ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default function AboutPage() {
  usePageMeta({
    title: "About Viall Business Solutions | Local North Dakota Benefits Brokers",
    description: "Meet the team behind Viall Business Solutions. Local, independent benefits brokers based in Minot, ND, helping small businesses build better employee benefits packages.",
    path: "/about",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <section data-testid="about-hero" className="bg-[#1A2744] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>About Us</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">A local team dedicated to helping North Dakota businesses thrive through better employee benefits.</p>
        </div>
      </section>
      <MissionSection />
      <TeamSection />
      <WhyChooseUsSection />
      <WhoWeServeSection />
    </div>
  );
}
